/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR304: Deal Card Display Helpers
 * 
 * Centralized logic for resolving display values for deal cards:
 * - Title: The primary identity (gift card brand being purchased)
 * - Condition: Where/how to get the deal
 * - Reward: What you receive
 * 
 * These helpers prevent malformed data like "20 Walmart" from appearing
 * as titles by filtering out numeric prefixes and prioritizing correct fields.
 */

import type { Deal } from "@/lib/types/deals";
import type { UIDeal } from "./toUiDeal";

// Type for deal-like objects (handles both Deal, UIDeal, and raw JSON)
// Using an interface with index signature for flexibility
type DealLike = (Partial<Deal> | Partial<UIDeal>) & {
  header_brand_name?: string | null;
  nested_purchase_brand?: string | null;
  purchase_gift_card_brand?: string | null;
  gift_card_brand?: string | null;
  qualifying_product_name?: string | null;
  reward_brand_name?: string | null;
  promo_merchant_name?: string | null;
  merchant?: string;
  qualifying_spend_cad?: number | null;
  spend_amount?: number | null;
  bonus_value_cad?: number | null;
  bonus_amount?: number | null;
  points_amount?: number | null;
  points_program_name?: string | null;
  deal_type?: string | null;
  deal_category?: string | null;
  headline_mechanic?: string | null;
};

/**
 * CR284: Detects if a string is purely numeric (e.g., "10000", "500.00")
 * These should never be used as brand names.
 */
export function isNumericOnly(s?: string | null): boolean {
  if (!s) return false;
  return /^\s*\d+(\.\d+)?\s*$/.test(s.trim());
}

/**
 * Detects if a string looks like a malformed brand name
 * e.g., "20 Walmart", "15 DINING CARD", "50.00 Starbucks"
 * CR284: Also catches purely numeric values like "10000"
 */
export function isProbablyBadBrand(s?: string | null): boolean {
  if (!s) return true;
  // CR284: Purely numeric strings are always bad brands
  if (isNumericOnly(s)) return true;
  // Catches strings that start with a number followed by space
  // e.g., "20 Walmart", "15.00 SomeStore"
  return /^\s*\d+(\.\d+)?\s+/.test(s);
}

/**
 * Strips numeric prefix from a brand name
 * e.g., "20 Walmart" → "Walmart", "15.50 Starbucks" → "Starbucks"
 */
export function stripNumericPrefix(s?: string | null): string | null {
  if (!s) return null;
  return s.replace(/^\s*\d+(\.\d+)?\s+/, "").trim() || null;
}

/**
 * CR308: Extract value from nested canonical_deal_json path.
 */
function extractCanonical(deal: DealLike, path: string): string | null {
  // Try to access canonical_deal_json if it exists
  const canonical = (deal as Record<string, unknown>).canonical_deal_json as Record<string, unknown> | undefined;
  if (!canonical) return null;
  
  const parts = path.split(".");
  let current: unknown = canonical;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return null;
    current = (current as Record<string, unknown>)[part];
  }
  
  return typeof current === "string" ? current : null;
}

/**
 * Get the primary title for a deal card.
 * 
 * CR308: Title resolution order (canonical JSON first):
 * 1. Top-level header_brand_name (server-computed staging/admin preview brand)
 * 2. Top-level nested_purchase_brand (variant purchase brand)
 * 3. canonical_deal_json.identity.purchase_gift_card_brand
 * 4. canonical_deal_json.qualifying_product_name
 * 5. canonical_deal_json.brands.purchase_brand
 * 6. canonical_deal_json.reward_brand_name
 * 7. canonical_deal_json.brands.bonus_brand
 * 8. Top-level purchase_gift_card_brand
 * 9. Top-level gift_card_brand
 * 10. Top-level qualifying_product_name
 * 11. Top-level reward_brand_name (cleaned)
 * 12. canonical_deal_json.headline_mechanic (last resort)
 * 13. Top-level headline_mechanic (last resort)
 * 14. merchant/promo_merchant_name
 * 15. "Deal" (ultimate fallback)
 * 
 * CR284: All numeric-only values are rejected and trigger fallback.
 */
export function getDealCardTitle(deal: DealLike): string {
  // CR284: Helper to validate brand value is safe for display
  const isSafeBrand = (s?: string | null): s is string => {
    if (!s) return false;
    if (isNumericOnly(s)) return false;
    if (isProbablyBadBrand(s)) return false;
    return true;
  };

  // 1-2. Staging/admin preview explicit brand fields
  if (isSafeBrand(deal.header_brand_name)) {
    return deal.header_brand_name;
  }
  if (isSafeBrand(deal.nested_purchase_brand)) {
    return deal.nested_purchase_brand;
  }
  
  // 3-7. Check canonical JSON paths first (nested structure)
  const canonicalPaths = [
    "identity.purchase_gift_card_brand",
    "qualifying_product_name",
    "brands.purchase_brand",
    "reward_brand_name",
    "brands.bonus_brand",
  ];
  
  for (const path of canonicalPaths) {
    const value = extractCanonical(deal, path);
    if (isSafeBrand(value)) {
      return value;
    }
  }
  
  // 8. Check top-level purchase gift card brand
  const purchaseBrand = deal.purchase_gift_card_brand;
  if (isSafeBrand(purchaseBrand)) {
    return purchaseBrand;
  }
  
  // 9. Check gift_card_brand (older field name)
  const giftCardBrand = deal.gift_card_brand;
  if (isSafeBrand(giftCardBrand)) {
    return giftCardBrand;
  }
  
  // 10. Check qualifying product name
  const qualifyingProduct = deal.qualifying_product_name;
  if (isSafeBrand(qualifyingProduct)) {
    return qualifyingProduct;
  }
  
  // 11. Check reward_brand_name (only if valid)
  const rewardBrand = deal.reward_brand_name;
  if (isSafeBrand(rewardBrand)) {
    return rewardBrand;
  }
  
  // Try to clean reward_brand_name by stripping numeric prefix
  const cleanedRewardBrand = stripNumericPrefix(rewardBrand);
  if (cleanedRewardBrand && !isNumericOnly(cleanedRewardBrand)) {
    return cleanedRewardBrand;
  }
  
  // 12-13. CR308: headline_mechanic is LAST RESORT - not a title
  const canonicalHeadline = extractCanonical(deal, "headline_mechanic");
  const headline = canonicalHeadline ?? deal.headline_mechanic;
  if (headline && !isNumericOnly(headline)) {
    return headline;
  }
  
  // 14. CR284: Safe fallback to merchant name (defense-in-depth)
  const merchantName = deal.promo_merchant_name ?? deal.merchant;
  if (merchantName && !isNumericOnly(merchantName)) {
    return merchantName;
  }
  
  // 15. Ultimate fallback
  return "Deal";
}

/**
 * Get the condition line for a deal card.
 * Shows where to buy and spend threshold.
 * 
 * Format: "Buy at [merchant]" or "Buy at [merchant] when you spend $X"
 * 
 * @returns null if no condition info available
 */
export function getDealCardCondition(deal: DealLike): string | null {
  const merchant = deal.promo_merchant_name ?? deal.merchant;
  const spend = deal.qualifying_spend_cad;
  
  // Also check legacy spend_amount field
  const spendAmount = spend ?? deal.spend_amount;
  
  if (!merchant && spendAmount == null) return null;
  
  const parts: string[] = [];
  
  if (merchant) {
    // Clean merchant name if it has numeric prefix
    const cleanMerchant = isProbablyBadBrand(merchant) 
      ? stripNumericPrefix(merchant) 
      : merchant;
    if (cleanMerchant) {
      parts.push(`Buy at ${cleanMerchant}`);
    }
  }
  
  if (spendAmount != null && spendAmount > 0) {
    parts.push(`when you spend $${Number(spendAmount).toFixed(0)}`);
  }
  
  return parts.length > 0 ? parts.join(" ") : null;
}

/**
 * Get the reward display for a deal card.
 * 
 * For BONUS deals: "Get $X bonus"
 * For POINTS deals: "Earn X,XXX [program] points"
 */
export function getDealCardReward(deal: DealLike): string | null {
  const dealType = (deal.deal_type ?? deal.deal_category)?.toString()?.toUpperCase();
  
  if (dealType === "BONUS") {
    const bonusValue = deal.bonus_value_cad ?? deal.bonus_amount;
    if (bonusValue != null && Number(bonusValue) > 0) {
      return `Get $${Number(bonusValue).toFixed(0)} bonus`;
    }
    return null;
  }
  
  if (dealType === "POINTS") {
    const pointsAmount = deal.points_amount;
    const programName = deal.points_program_name;
    
    if (pointsAmount != null && pointsAmount > 0) {
      const formattedPoints = pointsAmount.toLocaleString();
      if (programName) {
        return `Earn ${formattedPoints} ${programName} points`;
      }
      return `Earn ${formattedPoints} points`;
    }
    return null;
  }
  
  return null;
}

/**
 * CR415: Resolve the "reward merchant" for BONUS deals.
 * 
 * This is the retailer where the bonus gift card is available — NOT the issuer.
 * Precedence:
 * 1. deal.bonus_merchant (explicit bonus merchant from extraction)
 * 2. deal.promo_merchant_name (purchase retailer, preferred display)
 * 3. deal.merchant (fallback retailer)
 * 
 * Hard rule: Never use purchase_gift_card_brand, issuer_*, or issuer UUID.
 */
export function getRewardMerchantName(deal: DealLike): string | null {
  // 1. Explicit bonus merchant (only if truly present and not a bad brand)
  const bonusMerchant = (deal as any).bonus_merchant;
  if (bonusMerchant && typeof bonusMerchant === "string" && !isNumericOnly(bonusMerchant)) {
    const cleaned = isProbablyBadBrand(bonusMerchant) ? stripNumericPrefix(bonusMerchant) : bonusMerchant;
    if (cleaned) return cleaned;
  }
  
  // 2. Purchase retailer (promo_merchant_name preferred)
  const promoMerchant = deal.promo_merchant_name;
  if (promoMerchant && typeof promoMerchant === "string" && !isNumericOnly(promoMerchant)) {
    const cleaned = isProbablyBadBrand(promoMerchant) ? stripNumericPrefix(promoMerchant) : promoMerchant;
    if (cleaned) return cleaned;
  }
  
  // 3. Fallback merchant
  const merchant = deal.merchant;
  if (merchant && typeof merchant === "string" && !isNumericOnly(merchant)) {
    const cleaned = isProbablyBadBrand(merchant) ? stripNumericPrefix(merchant) : merchant;
    if (cleaned) return cleaned;
  }
  
  return null;
}

/**
 * CR415: Get the reward label for a deal.
 * 
 * For BONUS deals: "Bonus gift card (retailer)" or "Bonus gift card"
 * For other deal types: returns the existing reward label or null.
 */
export function getRewardLabel(deal: DealLike): string {
  const dealType = (deal.deal_type ?? deal.deal_category)?.toString()?.toUpperCase();
  
  if (dealType === "BONUS") {
    const rewardMerchant = getRewardMerchantName(deal);
    return rewardMerchant
      ? `Bonus gift card (${rewardMerchant})`
      : "Bonus gift card";
  }
  
  // CR417: Cross-category discount — show discount target in label
  if (dealType === "DISCOUNT") {
    const headline = deal.headline_mechanic;
    const finePrint = (deal as any).fine_print ?? extractCanonical(deal, "fine_print");
    
    if (headline) {
      const match = headline.match(/\$\d+(?:\.\d+)?\s+(?:off|OFF)\s+(\w[\w\s]*?)(?:\s+when|\s+with|\s+if|\.|,|$)/i)
        ?? headline.match(/save\s+\$\d+(?:\.\d+)?\s+on\s+(\w[\w\s]*?)(?:\s+when|\s+with|\s+if|\.|,|$)/i);
      if (match) {
        const target = match[1].trim();
        if (!/gift\s*card|bonus\s*card/i.test(target) && target.length > 2 && target.length < 40) {
          return `$${extractDiscountAmount(headline) ?? "?"} off ${target}`;
        }
      }
    }
    
    // CR417: Fine print fallback — "Minimum $30 flower spend" → target = "flower"
    if (finePrint) {
      const fpMatch = (finePrint as string).match(/minimum\s+\$?\d+(?:\.\d+)?\s+(\w[\w\s]*?)\s+spend/i);
      if (fpMatch) {
        const target = fpMatch[1].trim();
        if (!/gift\s*card|bonus\s*card/i.test(target) && target.length > 2 && target.length < 40) {
          const amount = headline ? extractDiscountAmount(headline) : null;
          return amount ? `$${amount} off ${target}` : `Discount on ${target}`;
        }
      }
    }
    
    return "Discount";
  }
  
  // Non-BONUS/DISCOUNT: return generic label
  return "Reward";
}

/**
 * CR417: Extract dollar discount amount from headline text.
 */
function extractDiscountAmount(headline: string): string | null {
  const match = headline.match(/\$(\d+(?:\.\d+)?)\s+(?:off|OFF)/i)
    ?? headline.match(/save\s+\$(\d+(?:\.\d+)?)/i);
  return match ? match[1] : null;
}

/**
 * Check if a deal is a gift card bonus deal.
 * Gift card bonus = deal_type is BONUS + has a valid gift card brand
 */
/**
 * CR417: Resolve the primary header brand (issuer-first).
 * 
 * The header should show the gift card brand being promoted, NOT the retailer.
 * Precedence:
 * 1. purchase_gift_card_brand (best: "the card you're buying")
 * 2. gift_card_brand (legacy)
 * 3. reward_brand_name (only if valid)
 * 4. qualifying_product_name (if reliably a brand)
 * 5. merchant (last resort — retailer fallback)
 */
export function getDealCardHeaderBrand(deal: DealLike): { name: string | null; source: string } {
  const isSafe = (s?: string | null): s is string => !!s && !isProbablyBadBrand(s);

  // CR412: issuer_display_name from enriched view (merchants join) takes top priority
  const issuerName = (deal as any).issuer_display_name;
  if (isSafe(issuerName)) return { name: issuerName, source: "issuer_display_name" };

  // 1. purchase_gift_card_brand — canonical identity
  const purchaseBrand = deal.purchase_gift_card_brand;
  if (isSafe(purchaseBrand)) return { name: purchaseBrand, source: "purchase_gift_card_brand" };

  // 2. gift_card_brand (legacy) — CR418: must be gift_card_brand, NOT purchase_gift_card_brand
  const gcBrand = deal.gift_card_brand;
  if (isSafe(gcBrand)) return { name: gcBrand, source: "gift_card_brand" };

  // 3. reward_brand_name (cleaned) — CR418: guard stripNumericPrefix against null
  const rewardBrand = deal.reward_brand_name;
  if (isSafe(rewardBrand)) return { name: rewardBrand, source: "reward_brand_name" };
  if (rewardBrand) {
    const cleanedReward = stripNumericPrefix(rewardBrand);
    if (cleanedReward && !isNumericOnly(cleanedReward)) return { name: cleanedReward, source: "reward_brand_name_cleaned" };
  }

  // 4. qualifying_product_name
  const qpn = deal.qualifying_product_name;
  if (isSafe(qpn)) return { name: qpn, source: "qualifying_product_name" };

  // 5. merchant_name ?? merchant (last resort) — CR418: standardize on merchant_name first
  const merchantName = (deal as any).merchant_name ?? deal.promo_merchant_name ?? deal.merchant;
  if (merchantName && !isNumericOnly(merchantName)) return { name: merchantName, source: "merchant_fallback" };

  return { name: null, source: "none" };
}

/**
 * CR417: Resolve secondary context merchant (where to buy / retailer).
 * 
 * Only meaningful when it differs from the header brand.
 */
export function getDealCardHeaderContextMerchant(deal: DealLike): { name: string | null; source: string } {
  // CR418: Standardize retailer context: merchant_name → promo_merchant_name → merchant
  const merchant = (deal as any).merchant_name ?? deal.promo_merchant_name ?? deal.merchant;
  if (!merchant || isNumericOnly(merchant)) return { name: null, source: "none" };

  // Only show context if it differs from the header brand (case-insensitive)
  const headerBrand = getDealCardHeaderBrand(deal);
  if (headerBrand.name && headerBrand.name.toLowerCase() === merchant.toLowerCase()) {
    return { name: null, source: "same_as_header" };
  }

  return { name: merchant, source: (deal as any).merchant_name ? "merchant_name" : (deal.promo_merchant_name ? "promo_merchant_name" : "merchant") };
}

export function isGiftCardBonusDeal(deal: DealLike): boolean {
  const dealType = (deal.deal_type ?? deal.deal_category)?.toString()?.toUpperCase();
  if (dealType !== "BONUS") return false;
  
  const purchaseBrand = deal.purchase_gift_card_brand;
  const giftCardBrand = deal.gift_card_brand;
  const brand = purchaseBrand || giftCardBrand;
  
  if (!brand) return false;
  
  // Exclude placeholder values
  const lowerBrand = brand.toLowerCase();
  if (lowerBrand.startsWith("select") || lowerBrand.startsWith("choose")) {
    return false;
  }
  
  return true;
}
